import { Injectable } from '@angular/core';

/**
 * Servicio para validación sintáctica y semántica de fórmulas lógicas.
 * 
 * Implementa un parser con árbol de expresión usando análisis descendente.
 * 
 * **Tokens soportados:**
 * - Variables (condiciones): Cualquier identificador alfanumérico
 * - Operadores: AND, OR, NOT
 * - Paréntesis: ( )
 * 
 * **Validaciones:**
 * - Estructura completa de la expresión
 * - Paréntesis balanceados
 * - Operadores correctamente colocados
 * - Variables válidas
 * 
 * @example
 * // Fórmula válida: "C1 AND C2 OR (C3 AND C4)"
 * // Fórmula inválida: "C1 AND OR C2" (operadores mal colocados)
 */
@Injectable({
  providedIn: 'root',
})
export class FormulaValidatorService {
  /**
   * Valida la sintaxis de una fórmula lógica.
   * 
   * @param {string} formula - La fórmula a validar
   * @returns {{ valid: boolean; error?: string }} Objeto con resultado de validación
   * 
   * @example
   * const result = validator.validateSyntax("C1 AND C2");
   * // { valid: true }
   * 
   * const result2 = validator.validateSyntax("C1 AND OR C2");
   * // { valid: false, error: "Operador mal colocado después de AND" }
   */
  validateSyntax(formula: string): { valid: boolean; error?: string } {
    if (!formula || formula.trim().length === 0) {
      return { valid: true }; // Fórmula vacía es válida
    }

    try {
      // Tokenizar la fórmula
      const tokens = this.tokenize(formula);
      
      if (tokens.length === 0) {
        return { valid: true };
      }

      // Validar paréntesis balanceados
      const parenthesesCheck = this.validateParentheses(tokens);
      if (!parenthesesCheck.valid) {
        return parenthesesCheck;
      }

      // Validar estructura sintáctica
      const syntaxCheck = this.validateSyntaxStructure(tokens);
      if (!syntaxCheck.valid) {
        return syntaxCheck;
      }

      return { valid: true };
    } catch (error: any) {
      return { valid: false, error: error.message || 'Error de validación' };
    }
  }

  /**
   * Tokeniza una fórmula en tokens individuales.
   * 
   * @private
   * @param {string} formula - La fórmula a tokenizar
   * @returns {Array<{type: string, value: string, position: number}>} Array de tokens
   */
  private tokenize(formula: string): Array<{ type: string; value: string; position: number }> {
    const tokens: Array<{ type: string; value: string; position: number }> = [];
    let currentPosition = 0;
    let i = 0;

    while (i < formula.length) {
      const char = formula[i];

      // Saltar espacios
      if (/\s/.test(char)) {
        i++;
        continue;
      }

      // Paréntesis
      if (char === '(' || char === ')') {
        tokens.push({
          type: char === '(' ? 'LPAREN' : 'RPAREN',
          value: char,
          position: currentPosition,
        });
        i++;
        currentPosition = i;
        continue;
      }

      // Identificar operadores y variables
      const remaining = formula.substring(i);
      
      // Intentar match con operadores (AND, OR, NOT)
      if (remaining.match(/^AND\b/i)) {
        tokens.push({ type: 'OPERATOR', value: 'AND', position: currentPosition });
        i += 3;
        currentPosition = i;
        continue;
      }
      
      if (remaining.match(/^OR\b/i)) {
        tokens.push({ type: 'OPERATOR', value: 'OR', position: currentPosition });
        i += 2;
        currentPosition = i;
        continue;
      }

      if (remaining.match(/^NOT\b/i)) {
        tokens.push({ type: 'OPERATOR', value: 'NOT', position: currentPosition });
        i += 3;
        currentPosition = i;
        continue;
      }

      // Variable (identificador alfanumérico)
      const variableMatch = remaining.match(/^[a-zA-Z0-9_]+/);
      if (variableMatch) {
        tokens.push({
          type: 'VARIABLE',
          value: variableMatch[0],
          position: currentPosition,
        });
        i += variableMatch[0].length;
        currentPosition = i;
        continue;
      }

      // Carácter no reconocido
      throw new Error(`Carácter no reconocido: "${char}" en posición ${i}`);
    }

    return tokens;
  }

  /**
   * Valida que los paréntesis estén balanceados.
   * 
   * @private
   * @param {Array} tokens - Array de tokens
   * @returns {{ valid: boolean; error?: string }}
   */
  private validateParentheses(
    tokens: Array<{ type: string; value: string }>
  ): { valid: boolean; error?: string } {
    let depth = 0;
    for (const token of tokens) {
      if (token.type === 'LPAREN') {
        depth++;
      } else if (token.type === 'RPAREN') {
        depth--;
        if (depth < 0) {
          return { valid: false, error: 'Paréntesis de cierre sin apertura' };
        }
      }
    }

    if (depth > 0) {
      return { valid: false, error: 'Paréntesis de apertura sin cierre' };
    }

    return { valid: true };
  }

  /**
   * Valida la estructura sintáctica de los tokens.
   * 
   * @private
   * @param {Array} tokens - Array de tokens
   * @returns {{ valid: boolean; error?: string }}
   */
  private validateSyntaxStructure(
    tokens: Array<{ type: string; value: string; position: number }>
  ): { valid: boolean; error?: string } {
    if (tokens.length === 0) {
      return { valid: true };
    }

    // La expresión debe empezar con variable, NOT o paréntesis de apertura
    const firstToken = tokens[0];
    const isUnaryStart = firstToken.type === 'OPERATOR' && firstToken.value === 'NOT';
    if (!isUnaryStart && firstToken.type !== 'VARIABLE' && firstToken.type !== 'LPAREN') {
      return {
        valid: false,
        error: 'La fórmula debe empezar con una condición, NOT o paréntesis de apertura',
      };
    }

    // La expresión debe terminar con variable o paréntesis de cierre
    const lastToken = tokens[tokens.length - 1];
    if (lastToken.type !== 'VARIABLE' && lastToken.type !== 'RPAREN') {
      return {
        valid: false,
        error: 'La fórmula debe terminar con una condición o paréntesis de cierre',
      };
    }

    // Validar secuencia de tokens
    for (let i = 0; i < tokens.length - 1; i++) {
      const current = tokens[i];
      const next = tokens[i + 1];

      // Después de un operador:
      // - NOT: debe venir variable, NOT o paréntesis de apertura
      // - AND/OR: debe venir variable, NOT o paréntesis de apertura
      if (current.type === 'OPERATOR') {
        const nextIsNot = next.type === 'OPERATOR' && next.value === 'NOT';
        if (current.value === 'NOT') {
          if (next.type !== 'VARIABLE' && next.type !== 'LPAREN' && !nextIsNot) {
            return {
              valid: false,
              error: 'Operador "NOT" mal colocado. Debe seguir una condición, NOT o paréntesis de apertura',
            };
          }
        } else if (next.type !== 'VARIABLE' && next.type !== 'LPAREN' && !nextIsNot) {
          return {
            valid: false,
            error: `Operador "${current.value}" mal colocado. Debe seguir una condición, NOT o paréntesis de apertura`,
          };
        }
      }

      // Después de una variable, debe venir operador o paréntesis de cierre
      if (current.type === 'VARIABLE') {
        if (next.type !== 'OPERATOR' && next.type !== 'RPAREN') {
          return {
            valid: false,
            error: `Condición "${current.value}" debe seguir un operador o paréntesis de cierre`,
          };
        }
        if (next.type === 'OPERATOR' && next.value === 'NOT') {
          return {
            valid: false,
            error: 'El operador "NOT" solo puede ir al inicio, tras otro operador o tras paréntesis de apertura',
          };
        }
      }

      // Después de paréntesis de apertura, debe venir variable, NOT o paréntesis de apertura
      if (current.type === 'LPAREN') {
        const nextIsNot = next.type === 'OPERATOR' && next.value === 'NOT';
        if (next.type !== 'VARIABLE' && next.type !== 'LPAREN' && !nextIsNot) {
          return {
            valid: false,
            error: 'Después de paréntesis de apertura debe venir una condición, NOT o paréntesis de apertura',
          };
        }
      }

      // Después de paréntesis de cierre, debe venir operador o paréntesis de cierre
      if (current.type === 'RPAREN') {
        if (next.type !== 'OPERATOR' && next.type !== 'RPAREN') {
          return {
            valid: false,
            error: 'Después de paréntesis de cierre debe venir un operador o paréntesis de cierre',
          };
        }
        if (next.type === 'OPERATOR' && next.value === 'NOT') {
          return {
            valid: false,
            error: 'El operador "NOT" solo puede ir al inicio, tras otro operador o tras paréntesis de apertura',
          };
        }
      }
    }

    return { valid: true };
  }

  /**
   * Valida que todas las variables (condiciones) en la fórmula existan en la lista de condiciones.
   * 
   * @param {string} formula - La fórmula a validar
   * @param {string[]} availableConditions - Lista de IDs de condiciones disponibles
   * @returns {{ valid: boolean; error?: string; missingConditions?: string[] }}
   */
  validateSemantics(
    formula: string,
    availableConditions: string[]
  ): { valid: boolean; error?: string; missingConditions?: string[] } {
    if (!formula || formula.trim().length === 0) {
      return { valid: true };
    }

    try {
      const tokens = this.tokenize(formula);
      const variables = tokens
        .filter((t) => t.type === 'VARIABLE')
        .map((t) => t.value);

      const missingConditions = variables.filter(
        (v) => !availableConditions.includes(v)
      );

      if (missingConditions.length > 0) {
        return {
          valid: false,
          error: `Condiciones no encontradas: ${missingConditions.join(', ')}`,
          missingConditions,
        };
      }

      return { valid: true };
    } catch (error: any) {
      return { valid: false, error: error.message || 'Error de validación semántica' };
    }
  }
}

